import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, DollarSign, Building2, Save, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const PaymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({
    stripe: false,
    paypal: false
  });
  
  const [settings, setSettings] = useState({
    // Stripe
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    
    // PayPal
    paypal_enabled: false,
    paypal_client_id: '',
    paypal_secret: '',
    paypal_mode: 'sandbox',
    
    // Bank Transfer
    bank_transfer_enabled: false,
    bank_name: '',
    account_holder: '',
    iban: '',
    bic: '',
    bank_instructions: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/admin/payment-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/admin/payment-settings', settings);
      toast.success('Zahlungseinstellungen erfolgreich gespeichert');
      fetchSettings(); // Reload to get masked keys
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-center py-12">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-5xl mx-auto">
        <AdminPageHeader 
          title="Zahlungseinstellungen" 
          subtitle="Stripe, PayPal und Überweisung konfigurieren"
          icon={CreditCard}
        />

        {/* Section: Payment Methods - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F8F0FF]">
            <CreditCard className="h-4 w-4 text-[#A070D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Zahlungsmethoden</h2>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Stripe Settings */}
          <Card className="rounded-none">
            <CardHeader className="bg-violet-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-none bg-violet-100/70 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-violet-600" />
                  </div>
                  <div>
                    <CardTitle>Stripe</CardTitle>
                    <CardDescription>Kreditkarten-Zahlungen akzeptieren</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings.stripe_enabled}
                  onCheckedChange={(checked) => handleInputChange('stripe_enabled', checked)}
                />
              </div>
            </CardHeader>
            {settings.stripe_enabled && (
              <CardContent className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="stripe_publishable_key">Publishable Key</Label>
                  <Input
                    id="stripe_publishable_key"
                    value={settings.stripe_publishable_key || ''}
                    onChange={(e) => handleInputChange('stripe_publishable_key', e.target.value)}
                    placeholder="pk_test_..."
                    className="rounded-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Beginnt mit pk_test_ (Test) oder pk_live_ (Live)
                  </p>
                </div>
                <div>
                  <Label htmlFor="stripe_secret_key">Secret Key</Label>
                  <div className="relative">
                    <Input
                      id="stripe_secret_key"
                      type={showKeys.stripe ? "text" : "password"}
                      value={settings.stripe_secret_key || ''}
                      onChange={(e) => handleInputChange('stripe_secret_key', e.target.value)}
                      placeholder="sk_test_..."
                      className="rounded-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKeys(prev => ({ ...prev, stripe: !prev.stripe }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showKeys.stripe ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Beginnt mit sk_test_ (Test) oder sk_live_ (Live)
                  </p>
                </div>
                <div className="bg-sky-50/70 border border-sky-100 rounded-none p-4">
                  <p className="text-sm text-sky-800 mb-3">
                    <strong>🔑 Stripe API-Keys erhalten:</strong>
                  </p>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside mb-3">
                    <li>Registrieren oder anmelden bei Stripe</li>
                    <li>Kopieren Sie Ihren Publishable Key und Secret Key</li>
                    <li>Fügen Sie beide Keys hier ein</li>
                  </ol>
                  <div className="flex flex-wrap gap-2">
                    <a 
                      href="https://dashboard.stripe.com/register" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Stripe-Konto erstellen →
                    </a>
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center px-4 py-2 bg-white border border-indigo-300 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      API-Keys abrufen →
                    </a>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* PayPal Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>PayPal</CardTitle>
                    <CardDescription>Zahlungen über PayPal akzeptieren</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings.paypal_enabled}
                  onCheckedChange={(checked) => handleInputChange('paypal_enabled', checked)}
                />
              </div>
            </CardHeader>
            {settings.paypal_enabled && (
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="paypal_mode">Modus</Label>
                  <select
                    id="paypal_mode"
                    value={settings.paypal_mode || 'sandbox'}
                    onChange={(e) => handleInputChange('paypal_mode', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="sandbox">Sandbox (Test)</option>
                    <option value="live">Live (Produktion)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="paypal_client_id">Client ID</Label>
                  <Input
                    id="paypal_client_id"
                    value={settings.paypal_client_id || ''}
                    onChange={(e) => handleInputChange('paypal_client_id', e.target.value)}
                    placeholder="Ihre PayPal Client ID"
                  />
                </div>
                <div>
                  <Label htmlFor="paypal_secret">Secret</Label>
                  <div className="relative">
                    <Input
                      id="paypal_secret"
                      type={showKeys.paypal ? "text" : "password"}
                      value={settings.paypal_secret || ''}
                      onChange={(e) => handleInputChange('paypal_secret', e.target.value)}
                      placeholder="Ihr PayPal Secret"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKeys(prev => ({ ...prev, paypal: !prev.paypal }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showKeys.paypal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>PayPal API-Credentials erhalten:</strong><br />
                    1. Gehen Sie zu <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="underline">developer.paypal.com</a><br />
                    2. Erstellen Sie eine App<br />
                    3. Kopieren Sie Client ID und Secret<br />
                    4. Wählen Sie "Sandbox" für Tests oder "Live" für echte Zahlungen
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Bank Transfer Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle>Vorkasse / Banküberweisung</CardTitle>
                    <CardDescription>Zahlungen per Überweisung akzeptieren</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings.bank_transfer_enabled}
                  onCheckedChange={(checked) => handleInputChange('bank_transfer_enabled', checked)}
                />
              </div>
            </CardHeader>
            {settings.bank_transfer_enabled && (
              <CardContent className="space-y-4">
                {/* Info Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-amber-800">
                    <strong>So funktioniert Vorkasse:</strong><br />
                    Der Kunde erhält nach der Bestellung Ihre Bankdaten per E-Mail. 
                    Sobald das Geld auf Ihrem Konto eingegangen ist, versenden Sie die Bestellung.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="account_holder">Kontoinhaber *</Label>
                    <Input
                      id="account_holder"
                      value={settings.account_holder || ''}
                      onChange={(e) => handleInputChange('account_holder', e.target.value)}
                      placeholder="Vor- und Nachname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank_name">Bankname *</Label>
                    <Input
                      id="bank_name"
                      value={settings.bank_name || ''}
                      onChange={(e) => handleInputChange('bank_name', e.target.value)}
                      placeholder="z.B. Sparkasse, Volksbank, etc."
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="iban">IBAN *</Label>
                  <Input
                    id="iban"
                    value={settings.iban || ''}
                    onChange={(e) => handleInputChange('iban', e.target.value)}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="bic">BIC/SWIFT</Label>
                  <Input
                    id="bic"
                    value={settings.bic || ''}
                    onChange={(e) => handleInputChange('bic', e.target.value)}
                    placeholder="z.B. COBADEFFXXX"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="bank_instructions">Zahlungshinweise für den Kunden</Label>
                  <Textarea
                    id="bank_instructions"
                    value={settings.bank_instructions || 'Bitte überweisen Sie den Betrag innerhalb von 7 Tagen auf unser Konto. Geben Sie als Verwendungszweck Ihre Bestellnummer an. Sobald das Geld eingegangen ist, wird Ihre Bestellung versendet.'}
                    onChange={(e) => handleInputChange('bank_instructions', e.target.value)}
                    placeholder="Hinweise für den Kunden..."
                    rows={4}
                  />
                </div>

                {/* Preview */}
                {(settings.account_holder || settings.iban) && (
                  <div className="bg-gray-50 border rounded-lg p-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Vorschau (So sieht es der Kunde):</p>
                    <div className="bg-white rounded-lg p-4 border">
                      <h4 className="font-semibold text-gray-900 mb-3">Bankverbindung für Ihre Überweisung:</h4>
                      <div className="space-y-1 text-sm">
                        {settings.account_holder && <p><span className="text-gray-500">Kontoinhaber:</span> <strong>{settings.account_holder}</strong></p>}
                        {settings.bank_name && <p><span className="text-gray-500">Bank:</span> <strong>{settings.bank_name}</strong></p>}
                        {settings.iban && <p><span className="text-gray-500">IBAN:</span> <strong className="font-mono">{settings.iban}</strong></p>}
                        {settings.bic && <p><span className="text-gray-500">BIC:</span> <strong className="font-mono">{settings.bic}</strong></p>}
                      </div>
                      {settings.bank_instructions && (
                        <p className="mt-3 text-sm text-gray-600 border-t pt-3">
                          {settings.bank_instructions}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              size="lg"
              className="bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white hover:opacity-90"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichert...' : 'Einstellungen speichern'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
